
import React, { useEffect, useMemo, useState } from 'react';
import { GeneratedBook, User } from '../types';
import { storyService, logImageEvent } from '../services/storyService';
import { BookOpen, Trash2, Calendar, Wand2, Loader2 } from './Icons';
import { buildParentInsights } from '../services/storyInsights';

interface LibraryProps {
  user: User;
  onOpenBook: (book: GeneratedBook) => void;
  onCreateNew: () => void;
  onGoHome: () => void;
  childName?: string;
}

const Library: React.FC<LibraryProps> = ({ user, onOpenBook, onCreateNew, onGoHome, childName }) => {
  const [stories, setStories] = useState<GeneratedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const insights = useMemo(() => buildParentInsights(stories, childName), [stories, childName]);
  const latestStory = stories[0];

  useEffect(() => {
    let mounted = true;
    const loadStories = async () => {
        setLoading(true);
        const data = await storyService.getStories(user.id);
        if (mounted) {
            setStories(data);
            setLoading(false);
        }
    };
    loadStories();
    return () => { mounted = false; };
  }, [user.id]);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this story? This cannot be undone.")) {
        // Optimistic UI update
        setStories(prev => prev.filter(s => s.id !== id));
        
        // Actual delete
        const updated = await storyService.deleteStory(user.id, id);
        setStories(updated);
    }
  };

  if (loading) {
      return (
          <div className="container mx-auto px-6 py-20 flex justify-center text-white">
              <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
                  <p>Loading library...</p>
              </div>
          </div>
      );
  }

  return (
    <div className="container mx-auto px-6 py-10 animate-slide-up pb-24">
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-10 gap-4">
        <div>
          <button
            onClick={onGoHome}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-2"
          >
            ← Home
          </button>
          <h2 className="text-4xl font-serif font-bold text-white mb-2">My Library</h2>
          <p className="text-gray-400">
             {stories.length} / 10 stories saved
          </p>
        </div>

        <button
          onClick={onCreateNew}
          className="bg-white text-black px-6 py-3 rounded-full font-bold hover:bg-gray-200 transition-colors flex items-center gap-2 shadow-lg"
        >
          <Wand2 className="w-4 h-4" />
          Create New
        </button>
      </div>

      {stories.length > 0 && (
        <section className="mb-10 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-2xl font-serif font-bold text-white">Parent Insights</h3>
              <p className="text-gray-400 text-sm">Read-only explainability snapshot from recent stories.</p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 font-bold">No rankings. No pressure.</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-2">Themes Read</p>
              <p className="text-white font-semibold">{insights.topThemes.map((t) => t.label).join(', ') || 'Not enough data yet'}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-2">Emotional Tones</p>
              <p className="text-white font-semibold">{insights.topTones.map((t) => t.label).join(', ') || 'Not enough data yet'}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-2">Values Reinforced</p>
              <p className="text-white font-semibold">{insights.topValues.map((v) => v.label).join(', ') || 'Not enough data yet'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gold-500/30 bg-gold-500/10 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold-300 mb-2">Why This Story Was Generated</p>
              <p className="text-sm text-white/90">{insights.whyGenerated}</p>
            </div>
            <div className="rounded-2xl border border-spark-500/30 bg-spark-500/10 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-spark-300 mb-2">Value Emphasized</p>
              <p className="text-sm text-white/90">{latestStory ? insights.emphasizedValue : 'Generate a story to see value emphasis.'}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-300 mb-2">What Changed In The World</p>
              <p className="text-sm text-white/80">World memory arrives in v2.1. For now, this view reflects per-story themes, tones, and values.</p>
            </div>
          </div>
        </section>
      )}

      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-500">
                <BookOpen className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Your library is empty</h3>
            <p className="text-gray-400 mb-6">Create your first magical story today!</p>
            <button 
                onClick={onCreateNew}
                className="text-gold-400 font-bold hover:text-gold-300 transition-colors"
            >
                Start Creating &rarr;
            </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {stories.map((book, idx) => (
                <div 
                    key={book.id || idx}
                    onClick={() => onOpenBook(book)}
                    className="group relative aspect-[3/4] bg-spark-900 rounded-xl shadow-book overflow-hidden cursor-pointer hover:transform hover:-translate-y-2 transition-all duration-300 border border-white/10"
                >
                    {/* Cover Image */}
                    {book.coverImageUrl ? (
                        <img
                            src={book.coverImageUrl}
                            alt={book.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onLoad={() => logImageEvent('load_success', book.coverImageUrl!, book.id, -1, { type: 'library-cover' })}
                            onError={() => logImageEvent('load_error', book.coverImageUrl!, book.id, -1, { type: 'library-cover' })}
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-spark-800 to-black p-6 flex items-center justify-center text-center">
                            <span className="font-serif italic text-white/50">{book.title}</span>
                        </div>
                    )}

                    {/* Overlay Info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-6 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                        <h3 className="text-xl font-bold font-serif text-white mb-1 leading-tight">{book.title}</h3>
                        <p className="text-xs text-gray-300 line-clamp-2 mb-4 italic">{book.synopsis}</p>
                        
                        <div className="flex justify-between items-center mt-2">
                             <div className="flex items-center gap-1 text-xs text-gray-400">
                                <Calendar className="w-3 h-3" />
                                <span>{book.createdAt ? new Date(book.createdAt).toLocaleDateString() : 'Recent'}</span>
                             </div>
                             <button 
                                onClick={(e) => handleDelete(e, book.id!)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-full hover:bg-red-500 hover:text-white transition-colors"
                                title="Delete Story"
                             >
                                <Trash2 className="w-4 h-4" />
                             </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default Library;
